import React, { useState } from 'react';
import { 
  X, Lock, Mail, Building, UserCheck, ArrowRight, ShieldCheck, 
  Sparkles, CheckCircle2, AlertCircle 
} from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: { name: string; email: string; role: string; tenant: string }) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [role, setRole] = useState('TENANT_ADMIN');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleQuickDemoLogin = (demoUser: { name: string; email: string; role: string; tenant: string }) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onLoginSuccess(demoUser);
      onClose();
    }, 600);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all required credentials.');
      return;
    }
    setLoading(true);
    setError('');

    // Simulate API Login / Sanctum Token Exchange
    setTimeout(() => {
      setLoading(false);
      onLoginSuccess({
        name: email.split('@')[0].toUpperCase(),
        email: email,
        role: role,
        tenant: tenantName || 'Default Organization'
      });
      onClose();
    }, 800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden relative">
        
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 p-6 text-white relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-blue-200 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-bold text-white text-lg">
              A
            </div>
            <span className="font-extrabold tracking-wide text-lg">AbilityOS Identity</span>
          </div>

          <h2 className="text-2xl font-bold">
            {isSignUp ? 'Create Workspace' : 'Sign in to AbilityOS'}
          </h2>
          <p className="text-xs text-blue-100 mt-1">
            {isSignUp ? 'Setup multi-tenant special education portal' : 'Access your therapy & IEP management platform'}
          </p>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-xs font-medium">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
              <span>{error}</span>
            </div>
          )}

          {/* Preset Demo Accounts Quick Login */}
          {!isSignUp && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-slate-500">
                <span className="flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  1-Click Quick Demo Login
                </span>
                <span className="text-[10px] text-blue-600">No password required</span>
              </div>
              
              <div className="grid grid-cols-1 gap-1.5">
                <button
                  type="button"
                  onClick={() => handleQuickDemoLogin({
                    name: 'Super Admin',
                    email: 'superadmin@bugfreesolution.com',
                    role: 'SUPER_ADMIN',
                    tenant: 'WHM / cPanel Root Control'
                  })}
                  className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-blue-500 hover:shadow-sm text-left transition"
                >
                  <div>
                    <div className="text-xs font-bold text-slate-800">🛡️ WHM Super Admin</div>
                    <div className="text-[11px] text-slate-500">Full system control plane</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-blue-600" />
                </button>

                <button
                  type="button"
                  onClick={() => handleQuickDemoLogin({
                    name: 'St. Jude Principal',
                    email: 'principal@stjude.edu',
                    role: 'TENANT_ADMIN',
                    tenant: 'St. Jude Special School'
                  })}
                  className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-blue-500 hover:shadow-sm text-left transition"
                >
                  <div>
                    <div className="text-xs font-bold text-slate-800">🏫 School Administrator</div>
                    <div className="text-[11px] text-slate-500">Manage teachers, IEPs & students</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-blue-600" />
                </button>

                <button
                  type="button"
                  onClick={() => handleQuickDemoLogin({
                    name: 'Dr. Sarah Connor',
                    email: 'therapist@sunshine.org',
                    role: 'THERAPIST',
                    tenant: 'Sunshine Therapy Centre'
                  })}
                  className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-blue-500 hover:shadow-sm text-left transition"
                >
                  <div>
                    <div className="text-xs font-bold text-slate-800">🩺 Lead Speech Therapist</div>
                    <div className="text-[11px] text-slate-500">AAC Board & SOAP Progress Notes</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-blue-600" />
                </button>
              </div>
            </div>
          )}

          {/* Credentials Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {isSignUp && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Organization / School Name</label>
                <div className="relative">
                  <Building className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    required
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                    placeholder="e.g. Apex Rehabilitation Center"
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@organization.com"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {isSignUp && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Account Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="TENANT_ADMIN">Organization Administrator</option>
                  <option value="THERAPIST">Therapist / Educator</option>
                  <option value="SUPER_ADMIN">Super Administrator (WHM Root)</option>
                </select>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-sm shadow-md transition flex items-center justify-center gap-2"
            >
              {loading ? (
                <span>Authenticating with Sanctum API...</span>
              ) : (
                <>
                  <UserCheck className="w-4 h-4" />
                  {isSignUp ? 'Complete Registration' : 'Sign In to Portal'}
                </>
              )}
            </button>
          </form>

          {/* Toggle Sign Up / Sign In */}
          <div className="text-center pt-2 border-t border-slate-100">
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-xs font-semibold text-blue-600 hover:underline"
            >
              {isSignUp 
                ? 'Already have an account? Sign In' 
                : "Don't have a workspace? Register new organization"}
            </button>
          </div>
        </div>

        {/* Footer Security Badge */}
        <div className="bg-slate-100 px-6 py-3 flex items-center justify-center gap-2 text-[11px] text-slate-500 border-t border-slate-200">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          <span>Secured via Laravel Sanctum REST Bearer Auth & HIPPA Standard Encryption</span>
        </div>

      </div>
    </div>
  );
};
